<template>
    <FormSection title="Advance Directives" :full-height="false" :application-id="application" class="mb-2">
        <template #content>
            <section class="col p-3">
                <div class="d-flex align-items-center justify-content-between">
                    <p class="field-title">
                        Does this patient have an advanced care plan or a surrogate decision maker AND able to provide
                        legal documentation for the hospice medical record?
                    </p>
                    <button type="button" class="btn btn-primary" @click="openAddDocumentModal()">Add Document</button>
                </div>
                <div class="d-flex">
                    <div class="form-check custom-radio form-check-inline">
                        <input
                            class="form-check-input"
                            type="radio"
                            name="advanceCarePlan"
                            id="advanceCarePlanOption1"
                            v-model="patientDetails.advancedDirective.hasLegalDocumentation"
                            :value="true"
                        />
                        <label class="form-check-label" for="advanceCarePlanOption1"> Yes </label>
                    </div>
                    <div class="form-check custom-radio form-check-inline">
                        <input
                            class="form-check-input"
                            type="radio"
                            name="advanceCarePlan"
                            id="advanceCarePlanOption2"
                            v-model="patientDetails.advancedDirective.hasLegalDocumentation"
                            :value="false"
                        />
                        <label class="form-check-label" for="advanceCarePlanOption2"> No </label>
                    </div>
                </div>
                <template v-if="patientDetails.advancedDirective.hasLegalDocumentation">
                    <div class="field-title my-3">Advance Directives</div>
                    <div
                        v-for="[key, value] in enumToMap(HosAdvanceDirectivesEnum)"
                        :key="`${value.value}-${value.name}`"
                        class="form-check custom-checkbox mt-1"
                    >
                        <input
                            class="form-check-input"
                            type="checkbox"
                            :id="`${value.name}-${value.value}`"
                            :value="value.value"
                            @change="handleAdvDirectivesSelection(value.value)"
                            :checked="patientDetails.advancedDirective.advancedCarePlanDirectives.includes(value.value)"
                        />
                        <label class="form-check-label" :for="`${value.name}-${value.value}`">{{ key }}</label>
                    </div>
                </template>

                <hr />

                <div class="mt-2">
                    <div class="field-title">Code Status</div>
                    <div
                        v-for="[key, value] in enumToMap(HosCodeStatusEnum)"
                        :key="`${value.value}-${value.name}`"
                        class="form-check custom-checkbox mt-1"
                    >
                        <input
                            class="form-check-input"
                            type="checkbox"
                            :id="`${value.name}-${value.value}`"
                            :value="value.value"
                            @change="handleCodeStatusSelection(value.value)"
                            :checked="patientDetails.advancedDirective.codeStatus.includes(value.value)"
                        />
                        <label class="form-check-label" :for="`${value.name}-${value.value}`">{{ key }}</label>
                    </div>
                        </div>
                <hr />

                <div class="form-group mt-2">
                    <label for="surrogate-decision-maker">Surrogate Decision Maker Section</label>
                    <input
                        class="form-control"
                        placeholder="Enter Surrogate Decision Maker"
                        v-model="patientDetails.advancedDirective.surrogateDecisionMaker"
                    />
                </div>
                <hr />

                <div>
                    <p class="field-title">
                        Is any of the following information documented in the patient's advanced plan of care?
                    </p>
                    <div class="form-group mt-2">
                        <label for="medical-treatment">Medical Treatment Preferences</label>
                        <input
                            type="text"
                            class="form-control"
                            placeholder="Enter Treatment Preferences"
                            v-model="patientDetails.advancedDirective.medicalPreferences"
                        />
                    </div>

                    <div class="form-group mt-2">
                        <label for="mental-health">Mental Health/Behavioral Treatment Preferences</label>
                        <input
                            type="text"
                            class="form-control"
                            placeholder="Enter Mental Health/Behavioral Treatment Preferences"
                            v-model="patientDetails.advancedDirective.mentalPreferences"
                        />
                    </div>
                    <div class="form-group mt-2">
                        <label for="cultural">Cultural/Social Preferences</label>
                        <input
                            type="text"
                            class="form-control"
                            placeholder="Enter Cultural/Social Preferences"
                            v-model="patientDetails.advancedDirective.culturalPreferences"
                        />
                    </div>
                    <div class="form-group mt-2">
                        <label for="spiritual">Spiritual/Religious Preferences</label>
                        <input
                            type="text"
                            class="form-control"
                            placeholder="Enter Spiritual/Religious Preferences"
                            v-model="patientDetails.advancedDirective.spiritualPreferences"
                        />
                    </div>
                </div>
            </section>
        </template>
    </FormSection>

    <FormSection title="Funeral Home" :full-height="false">
        <template #content>
            <section class="col-12 col-lg-10 row p-3">
                <div class="col-12 col-lg-4 pe-lg-2 form-group">
                    <label for="funeral-address-line-2"> Auto-fill using vendor list </label>
                    <TypeaheadInput
                        :getData="getVendorList"
                        :value="selectedVendor"
                        v-on:valueChanged="updateVendor"
                        :init-value="selectedVendor"
                        placeholder="Search Vendor by Name..."
                    />
                </div>
                <div class="col-12 col-lg-4 pe-lg-2 d-flex align-items-end">
                    <a target="_blank" :href="`${_redirectURL}`" class="font-size-md mb-3"> Add Vendor </a>
                </div>
                <div class="w-100"></div>
                <div class="col-12 col-lg-4 pe-lg-2 form-group">
                    <label for="funeral-name"> Name </label>
                    <input
                        class="form-control"
                        maxlength="50"
                        type="text"
                        placeholder="Enter Name"
                        v-model="patientDetails.patientFuneralHome.name"
                    />
                </div>
                <div class="col-12 col-lg-4 pe-lg-2 form-group">
                    <label for="address-line-1"> Address </label>
                    <PlacesAutocomplete
                        v-model:street="patientDetails.patientFuneralHome.address"
                        v-model:city="patientDetails.patientFuneralHome.city"
                        v-model:state="patientDetails.patientFuneralHome.state"
                        v-model:zip="patientDetails.patientFuneralHome.zipCode"
                    >
                    </PlacesAutocomplete>
                </div>
                <div class="w-100"></div>
                <div class="col-12 col-lg-4 pe-lg-2 form-group">
                    <label for="city"> City </label>
                    <input
                        class="form-control"
                        maxlength="50"
                        type="text"
                        v-model="patientDetails.patientFuneralHome.city"
                        placeholder="Enter City"
                    />
                </div>
                <div class="col-12 col-lg-2 pe-lg-2 form-group">
                    <label for="state"> State </label>
                    <StateTypeahead
                        v-model:selected-state="selectedState"
                        v-model:prefill-state="patientDetails.patientFuneralHome.state"
                    />
                </div>
                <div class="col-12 col-lg-2 pe-lg-2 form-group">
                    <label for="zip"> ZIP </label>
                    <input
                        class="form-control"
                        maxlength="10"
                        type="text"
                        v-model="patientDetails.patientFuneralHome.zipCode"
                        placeholder="Enter Zip"
                        v-number-only
                    />
                </div>
                <div class="w-100"></div>
                <div class="col-12 col-lg-4 pe-lg-2 form-group">
                    <label for="preferred-phone"> Phone Number </label>
                    <input
                        type="text"
                        class="form-control phone-number-input"
                        :value="formatPhoneNumber(patientDetails.patientFuneralHome.phoneNumber)"
                        @input="valueUpdated($event.target.value, 'primary')"
                        maxlength="14"
                        placeholder="Enter Phone Number"
                    />
                </div>
                <div class="col-12 col-lg-4 pe-lg-2 form-group">
                    <label for="alternate-phone"> After Hours Phone Number </label>
                    <input
                        type="text"
                        class="form-control phone-number-input"
                        :value="formatPhoneNumber(patientDetails.patientFuneralHome.afterHrsPhoneNumber)"
                        @input="valueUpdated($event.target.value)"
                        maxlength="14"
                        placeholder="Enter After Hours Phone Number"
                    />
                </div>
            </section>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./AdvanceDirectives.ts" />
